/**
 * The supplier invoice writes that have been written down and may not have been answered (batch 8 T12c3, #3033): an invoice or
 * credit note create, the acceptance of a variance and a rejection.
 *
 * Each is a keyed command: the server binds the `Idempotency-Key` to the WHOLE request (route, actor, key, canonical body, target)
 * and answers a repeat from its record. That only helps if the repeat is the same key with the same payload to the same target, so
 * the exact request is persisted BEFORE it is sent, and read back from storage before every dispatch:
 *
 *  - accepted (2xx, also a replay): removed;
 *  - definitely refused (a 4xx the server read and stored): removed, the user corrects the intent and a NEW operation gets a new key;
 *  - ambiguous (lost connection, timeout, 5xx, 401/403/423, an unanswered earlier attempt): KEPT. The screen offers to send the
 *    identical request again, or to abandon it knowingly. It never sends the same intent under a fresh key, because if the first
 *    attempt committed that would create it twice.
 *
 * Storage is the source of truth, not memory: every read parses the owner's `sessionStorage` document (per tab). A browser that
 * refuses storage is `unavailable` and a document that cannot be read is `corrupt`; in both states no write is dispatched (an earlier
 * unknown request cannot be ruled out, and a new one could not be kept). A corrupt record is only dropped by a knowing user action.
 * Nothing in an entry is a secret: it holds the business payload the user typed, the route target, and the key (a random UUID). No
 * token, no cookie, no permission list. The payload is financial data, so the document belongs to the owner (tenant, user, session
 * family) that wrote it: another owner cannot find, overwrite, delete or send it, and the screens show it only while the owner holds
 * the permissions of the operation. The document is bounded (entries, size) and every entry is validated on read.
 *
 * Memory is a witness, never a substitute: each owner's page keeps a ledger of the unresolved requests it wrote or found (the exact
 * request and key). Before every send, storage is read and must hold exactly what the ledger holds. If the document was deleted,
 * truncated or replaced while the page lived, the journal is `lost` (or `corrupt`): nothing is sent, no new key is minted for the
 * unresolved intent, and the user can restore the exact request and key from the ledger (`restoreJournal`, written and read back)
 * before an explicit retry. A write that storage refused leaves the ledger as it was, so the page recovers once storage works.
 *
 * HARD LIMIT: the ledger lives in page memory. If the stored document is deleted AND the page is reloaded (or the tab is closed),
 * nothing remains that could say a request was sent, and the journal cannot detect that. That cannot be fixed in the browser; only
 * the server's own record of the document (the document list) can show whether such a request committed.
 *
 * Nothing here sends a request.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { ownerEquals, ownerId, type InvoiceOwner } from './invoiceSession';

export type OperationKind = 'create' | 'accept' | 'reject';
/** `lost`: this page wrote unresolved requests that the stored document no longer holds (deleted, truncated into another valid document, replaced). */
export type JournalStatus = 'ok' | 'unavailable' | 'corrupt' | 'lost';
export type PutStatus = 'stored' | 'conflict' | 'full' | 'corrupt' | 'lost' | 'storage_failed' | 'invalid';

export interface JournalOperation {
  readonly kind: OperationKind;
  /** The address within the owner's journal: one unresolved operation per purchase order (create) or invoice (decision). */
  readonly slot: string;
  /** The purchase order (create) or the invoice (accept, reject) the request is addressed to. */
  readonly target: string;
  /** The purchase order the operation belongs to (the panel lists unresolved operations per order). */
  readonly purchaseOrderId: string;
  /** What the user recognises: the supplier's invoice number. */
  readonly label: string;
  /** The exact route under `/api/v1`. Never rebuilt. */
  readonly path: string;
  /** The `Idempotency-Key`: fixed for the life of the operation. */
  readonly key: string;
  /** The exact payload. Never rebuilt, never edited. */
  readonly body: unknown;
  readonly startedAt: string;
  /** How many times this very request was sent. */
  readonly attempts: number;
  readonly owner: InvoiceOwner;
}

export interface JournalView {
  readonly status: JournalStatus;
  readonly operations: readonly JournalOperation[];
  /** Slots with a request on its way in this page (possibly started by a screen that has since unmounted). */
  readonly inFlight: ReadonlySet<string>;
  /** True when this page still holds exact requests in memory that `restoreJournal` can write back (status `lost` or `corrupt`). */
  readonly restorable: boolean;
}

export const MAX_JOURNAL_OPERATIONS = 10;
const MAX_DOCUMENT_CHARS = 512 * 1024;
const PREFIX = 'os-buddy:supplier-invoice-journal:v1:';

export const slotOf = (kind: OperationKind, target: string): string => (kind === 'create' ? `create:${target}` : `decide:${target}`);

export function pathOf(kind: OperationKind, target: string): string {
  const id = encodeURIComponent(target);
  if (kind === 'create') return `/inventory/purchase-orders/${id}/supplier-invoices`;
  return `/inventory/supplier-invoices/${id}/${kind}`;
}

export const EMPTY_VIEW: JournalView = Object.freeze({ status: 'ok', operations: [], inFlight: new Set<string>(), restorable: false });

// ---------------------------------------------------------------------------
// Validation of what is read back
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown, max: number): value is string => typeof value === 'string' && value.length > 0 && value.length <= max;
const ID = /^[A-Za-z0-9_-]{1,100}$/;
const HEX64 = /^[0-9a-f]{64}$/;
const onlyKeys = (value: Record<string, unknown>, allowed: readonly string[]): boolean => Object.keys(value).every((key) => allowed.includes(key));
const isInt = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value);

const DOCUMENT_KEYS = ['documentKind', 'invoiceNumber', 'invoiceDate', 'currency', 'shipping', 'stated', 'lines', 'previewToken'];
const LINE_KEYS = [
  'lineNumber',
  'purchaseOrderLineId',
  'description',
  'quantity',
  'unitPriceNetCents',
  'discountPercent',
  'vatPercent',
  'statedLineNetCents',
  'receiptAllocations',
  'creditLinks',
];

function validLine(line: unknown): boolean {
  if (!isRecord(line) || !onlyKeys(line, LINE_KEYS)) return false;
  const links = (value: unknown, idKey: string): boolean =>
    Array.isArray(value) && value.length <= 50 && value.every((link) => isRecord(link) && onlyKeys(link, [idKey, 'quantity']) && isText(link[idKey], 100) && isInt(link['quantity']));
  return (
    isInt(line['lineNumber']) &&
    (line['purchaseOrderLineId'] === null || isText(line['purchaseOrderLineId'], 100)) &&
    isText(line['description'], 200) &&
    isInt(line['quantity']) &&
    typeof line['unitPriceNetCents'] === 'number' &&
    Number.isFinite(line['unitPriceNetCents']) &&
    typeof line['discountPercent'] === 'number' &&
    typeof line['vatPercent'] === 'number' &&
    (line['statedLineNetCents'] === undefined || line['statedLineNetCents'] === null || isInt(line['statedLineNetCents'])) &&
    links(line['receiptAllocations'], 'receiptLineId') &&
    links(line['creditLinks'], 'returnLineId')
  );
}

const validators: Record<OperationKind, (body: unknown) => boolean> = {
  create: (body) =>
    isRecord(body) &&
    onlyKeys(body, DOCUMENT_KEYS) &&
    (body['documentKind'] === 'invoice' || body['documentKind'] === 'credit_note') &&
    isText(body['invoiceNumber'], 100) &&
    typeof body['invoiceDate'] === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(body['invoiceDate']) &&
    body['currency'] === 'EUR' &&
    isRecord(body['shipping']) &&
    isInt(body['shipping']['netCents']) &&
    isRecord(body['stated']) &&
    isInt(body['stated']['netTotalCents']) &&
    isInt(body['stated']['vatTotalCents']) &&
    isInt(body['stated']['grossTotalCents']) &&
    Array.isArray(body['lines']) &&
    body['lines'].length >= 1 &&
    body['lines'].length <= 200 &&
    body['lines'].every(validLine) &&
    typeof body['previewToken'] === 'string' &&
    HEX64.test(body['previewToken']),
  accept: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['reason', 'evidenceHash', 'expectedRevision']) &&
    isText(body['reason'], 500) &&
    typeof body['evidenceHash'] === 'string' &&
    HEX64.test(body['evidenceHash']) &&
    isInt(body['expectedRevision']),
  reject: (body) => isRecord(body) && onlyKeys(body, ['reason', 'expectedRevision']) && isText(body['reason'], 500) && isInt(body['expectedRevision']),
};

// eslint-disable-next-line no-control-regex -- the server's `CONTROL` class of its text schema
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * What a NEW write must satisfy on top of what a held entry must (`validators`): the server's text rule for a decision reason
 * (trimmed, not empty, at most 500, no control character). It applies only to `putOperation`. Reading is deliberately not
 * stricter: an entry an older client journaled is still held, shown and retried with its exact request, never discarded; the
 * server's answer then decides it.
 */
function freshWriteValid(kind: OperationKind, body: unknown): boolean {
  if (kind === 'create' || !isRecord(body)) return true;
  const reason = body['reason'];
  return typeof reason === 'string' && reason === reason.trim() && !CONTROL.test(reason);
}

const KINDS: readonly OperationKind[] = ['create', 'accept', 'reject'];

interface StoredOperation {
  kind: OperationKind;
  target: string;
  purchaseOrderId: string;
  label: string;
  key: string;
  body: unknown;
  startedAt: string;
  attempts: number;
}

/** Whether `raw` is a well formed document of `owner`; the owner is the document's, never an entry's own claim. */
function parseDocument(raw: string, owner: InvoiceOwner): StoredOperation[] | null {
  if (raw.length > MAX_DOCUMENT_CHARS) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed['v'] !== 1 || !isRecord(parsed['owner'])) return null;
  const claimed = parsed['owner'];
  if (!ownerEquals(owner, { tenantId: String(claimed['tenantId']), userId: String(claimed['userId']), sessionFamilyId: String(claimed['sessionFamilyId']) })) return null;
  const entries = parsed['operations'];
  if (!Array.isArray(entries) || entries.length > MAX_JOURNAL_OPERATIONS) return null;
  const slots = new Set<string>();
  const result: StoredOperation[] = [];
  for (const entry of entries as unknown[]) {
    if (!isRecord(entry)) return null;
    const { kind, target, purchaseOrderId, label, key, body, startedAt, attempts, path } = entry;
    if (typeof kind !== 'string' || !(KINDS as readonly string[]).includes(kind)) return null;
    const typedKind = kind as OperationKind;
    if (typeof target !== 'string' || !ID.test(target) || typeof purchaseOrderId !== 'string' || !ID.test(purchaseOrderId)) return null;
    if (path !== pathOf(typedKind, target)) return null;
    if (typeof label !== 'string' || label.length > 100 || !isText(key, 200)) return null;
    if (typeof startedAt !== 'string' || Number.isNaN(Date.parse(startedAt))) return null;
    if (!isInt(attempts) || attempts < 0 || attempts > 1000) return null;
    if (!validators[typedKind](body)) return null;
    const slot = slotOf(typedKind, target);
    if (slots.has(slot)) return null;
    slots.add(slot);
    result.push({ kind: typedKind, target, purchaseOrderId, label, key, body, startedAt, attempts });
  }
  return result;
}

const serialize = (owner: InvoiceOwner, operations: readonly StoredOperation[]): string =>
  JSON.stringify({
    v: 1,
    owner: { tenantId: owner.tenantId, userId: owner.userId, sessionFamilyId: owner.sessionFamilyId },
    operations: operations.map((operation) => ({ ...operation, path: pathOf(operation.kind, operation.target) })),
  });

// ---------------------------------------------------------------------------
// Storage access: storage is the truth, memory only caches the parse of one raw document
// ---------------------------------------------------------------------------

const addressOf = (owner: InvoiceOwner): string => `${PREFIX}${ownerId(owner)}`;

type Read = { status: 'ok'; raw: string | null; operations: StoredOperation[] } | { status: 'unavailable' } | { status: 'corrupt' };

function readStorage(owner: InvoiceOwner): Read {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(addressOf(owner));
  } catch {
    return { status: 'unavailable' };
  }
  if (raw === null) return { status: 'ok', raw, operations: [] };
  const operations = parseDocument(raw, owner);
  return operations === null ? { status: 'corrupt' } : { status: 'ok', raw, operations };
}

interface Book {
  inFlight: Map<string, string>;
  /** Unresolved operations this page knows of, by slot: what storage MUST still hold. Only a resolution or a knowing abandon removes one. */
  ledger: Map<string, StoredOperation>;
  /** The view for the last raw document seen, so an unchanged store gives an unchanged snapshot. */
  cache: { signature: string; view: JournalView } | null;
}

const books = new Map<string, Book>();
const listeners = new Set<() => void>();

function bookOf(owner: InvoiceOwner): Book {
  const id = ownerId(owner);
  let book = books.get(id);
  if (!book) {
    book = { inFlight: new Map(), ledger: new Map(), cache: null };
    books.set(id, book);
  }
  return book;
}

function notify(): void {
  for (const listener of [...listeners]) listener();
}

const sameRequest = (left: StoredOperation, right: StoredOperation): boolean =>
  left.kind === right.kind &&
  left.target === right.target &&
  left.purchaseOrderId === right.purchaseOrderId &&
  left.key === right.key &&
  JSON.stringify(left.body) === JSON.stringify(right.body);

interface Inspection {
  read: Read;
  status: JournalStatus;
  /** `ok`: the stored operations (equal to the ledger). `lost`: the ledger's. Otherwise none. */
  operations: StoredOperation[];
}

/**
 * Reads storage and holds it against the ledger. Operations only storage knows (found after a reload) join the ledger; an
 * operation the ledger knows that storage lacks or holds differently makes the journal `lost`.
 */
function inspect(owner: InvoiceOwner): Inspection {
  const read = readStorage(owner);
  if (read.status !== 'ok') return { read, status: read.status, operations: [] };
  const book = bookOf(owner);
  const stored = new Map(read.operations.map((operation) => [slotOf(operation.kind, operation.target), operation]));
  let lost = false;
  for (const [slot, known] of [...book.ledger]) {
    const found = stored.get(slot);
    if (!found || !sameRequest(found, known)) {
      lost = true;
    } else if (found.attempts > known.attempts) {
      book.ledger.set(slot, { ...known, attempts: found.attempts });
    }
  }
  for (const [slot, operation] of stored) if (!book.ledger.has(slot)) book.ledger.set(slot, operation);
  return lost ? { read, status: 'lost', operations: [...book.ledger.values()] } : { read, status: 'ok', operations: read.operations };
}

export function getJournalView(owner: InvoiceOwner | null): JournalView {
  if (owner === null) return EMPTY_VIEW;
  const book = bookOf(owner);
  const inspected = inspect(owner);
  const signature = JSON.stringify([
    inspected.status,
    inspected.read.status === 'ok' ? inspected.read.raw : null,
    [...book.inFlight.entries()],
    [...book.ledger.values()].map((operation) => [slotOf(operation.kind, operation.target), operation.key, operation.attempts]),
  ]);
  if (book.cache && book.cache.signature === signature) return book.cache.view;
  const view: JournalView = {
    status: inspected.status,
    operations: inspected.operations.map((operation) => ({
      ...operation,
      path: pathOf(operation.kind, operation.target),
      slot: slotOf(operation.kind, operation.target),
      owner,
    })),
    inFlight: new Set(book.inFlight.keys()),
    restorable: book.ledger.size > 0 && (inspected.status === 'lost' || inspected.status === 'corrupt'),
  };
  book.cache = { signature, view };
  return view;
}

export function subscribeJournal(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab of the same session never shares sessionStorage, but a script or a cleared store can change it.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/** The journal of `owner`, live: re-renders when an operation, a flight or the storage state changes. */
export function useJournalView(owner: InvoiceOwner | null): JournalView {
  const snapshot = useCallback(() => getJournalView(owner), [owner]);
  return useSyncExternalStore(subscribeJournal, snapshot, snapshot);
}

/** Tells every screen to read the store again (a blocked dispatch found it damaged or unreadable: the notice must show now). */
export function refreshJournal(): void {
  notify();
}

export interface NewOperation {
  kind: OperationKind;
  target: string;
  purchaseOrderId: string;
  label: string;
  key: string;
  body: unknown;
}

/** Writes `operations` and reads them back; false when the browser would not keep exactly this text. */
function writeDocument(owner: InvoiceOwner, operations: readonly StoredOperation[]): boolean {
  const address = addressOf(owner);
  try {
    if (operations.length === 0) {
      window.sessionStorage.removeItem(address);
      return window.sessionStorage.getItem(address) === null;
    }
    const text = serialize(owner, operations);
    if (text.length > MAX_DOCUMENT_CHARS) return false;
    window.sessionStorage.setItem(address, text);
    return window.sessionStorage.getItem(address) === text;
  } catch {
    return false;
  }
}

/**
 * Writes the request down BEFORE it is sent and reads it back. Never overwrites an unresolved operation of the same slot. Any
 * storage failure is `storage_failed`: the caller must not dispatch, because nothing was kept that could recover the key.
 */
export function putOperation(owner: InvoiceOwner, input: NewOperation): PutStatus {
  // What could never be read back is refused up front, before storage is touched.
  if (!validators[input.kind](input.body) || !freshWriteValid(input.kind, input.body) || !ID.test(input.target) || !ID.test(input.purchaseOrderId)) return 'invalid';
  const inspected = inspect(owner);
  if (inspected.status === 'corrupt') return 'corrupt';
  if (inspected.status === 'lost') return 'lost';
  if (inspected.status === 'unavailable') return 'storage_failed';
  const read = { operations: inspected.operations };
  const slot = slotOf(input.kind, input.target);
  if (read.operations.some((operation) => slotOf(operation.kind, operation.target) === slot)) return 'conflict';
  if (read.operations.length >= MAX_JOURNAL_OPERATIONS) return 'full';
  const entry: StoredOperation = {
    kind: input.kind,
    target: input.target,
    purchaseOrderId: input.purchaseOrderId,
    label: input.label.slice(0, 100),
    key: input.key,
    body: input.body,
    startedAt: new Date().toISOString(),
    attempts: 0,
  };
  const stored = writeDocument(owner, [...read.operations, entry]);
  if (!stored) {
    // Put storage back as it was; if even that fails the next read says `corrupt` or `unavailable`, never "empty".
    writeDocument(owner, read.operations);
    notify();
    return 'storage_failed';
  }
  // The ledger holds what storage holds (the read-back form), so a later comparison is exact.
  const kept = readStorage(owner);
  const held = kept.status === 'ok' ? kept.operations.find((operation) => slotOf(operation.kind, operation.target) === slot) : undefined;
  if (held) bookOf(owner).ledger.set(slot, held);
  notify();
  return 'stored';
}

/**
 * The exact operation as storage holds it NOW, or `null` when storage cannot be read, the document is damaged or the slot no longer
 * holds this key. A dispatch needs this: what is sent is what was persisted, not what memory remembers.
 */
export function readBack(owner: InvoiceOwner, slot: string, key: string): JournalOperation | null {
  const view = getJournalView(owner);
  if (view.status !== 'ok') return null;
  return view.operations.find((operation) => operation.slot === slot && operation.key === key) ?? null;
}

/** Counts one more send of exactly this request. Returns false when storage would not keep the count (the send must not happen). */
export function recordAttempt(owner: InvoiceOwner, slot: string, key: string): boolean {
  const inspected = inspect(owner);
  if (inspected.status !== 'ok') return false;
  const read = { operations: inspected.operations };
  let found = false;
  const next = read.operations.map((operation) => {
    if (slotOf(operation.kind, operation.target) !== slot || operation.key !== key) return operation;
    found = true;
    return { ...operation, attempts: operation.attempts + 1 };
  });
  if (!found) return false;
  const stored = writeDocument(owner, next);
  if (stored) {
    const known = bookOf(owner).ledger.get(slot);
    if (known && known.key === key) bookOf(owner).ledger.set(slot, { ...known, attempts: known.attempts + 1 });
  }
  notify();
  return stored;
}

/**
 * Compare-and-delete: only the operation that still holds `key` goes. It leaves the ledger only once storage no longer holds it
 * (removed and read back, or already gone). False when storage cannot be read or refused, or the operation was never known.
 */
export function removeOperation(owner: InvoiceOwner, slot: string, key: string): boolean {
  const read = readStorage(owner);
  if (read.status !== 'ok') return false;
  const book = bookOf(owner);
  const known = book.ledger.get(slot);
  const knownHere = known !== undefined && known.key === key;
  const inStorage = read.operations.some((operation) => slotOf(operation.kind, operation.target) === slot && operation.key === key);
  if (!inStorage) {
    if (!knownHere) return false;
    // Storage lost it already: the operation is resolved, the witness goes with it.
    book.ledger.delete(slot);
    notify();
    return true;
  }
  const next = read.operations.filter((operation) => !(slotOf(operation.kind, operation.target) === slot && operation.key === key));
  const stored = writeDocument(owner, next);
  if (stored) book.ledger.delete(slot);
  notify();
  return stored;
}

/**
 * Writes the exact requests this page still holds back into storage (read back), after the stored document was deleted, replaced or
 * damaged. Same requests, same keys, same attempts; nothing is sent. False when storage refuses or there is nothing to restore.
 */
export function restoreJournal(owner: InvoiceOwner): boolean {
  const inspected = inspect(owner);
  if (inspected.status !== 'lost' && inspected.status !== 'corrupt') return false;
  const book = bookOf(owner);
  if (book.ledger.size === 0) return false;
  const stored = writeDocument(owner, [...book.ledger.values()]);
  notify();
  return stored;
}

/** The user knowingly discarded a damaged record: its content was never used. */
export function dropCorruptJournal(owner: InvoiceOwner): boolean {
  if (readStorage(owner).status !== 'corrupt') return false;
  // Requests this page still holds are not damaged: they are restored, never discarded with the damaged record.
  if (bookOf(owner).ledger.size > 0) return false;
  try {
    window.sessionStorage.removeItem(addressOf(owner));
  } catch {
    return false;
  }
  notify();
  return true;
}

/** Marks a request as on its way. False when another request of the slot already is. */
export function beginFlight(owner: InvoiceOwner, slot: string, key: string): boolean {
  const book = bookOf(owner);
  if (book.inFlight.has(slot)) return false;
  book.inFlight.set(slot, key);
  notify();
  return true;
}

export function endFlight(owner: InvoiceOwner, slot: string, key: string): void {
  const book = bookOf(owner);
  if (book.inFlight.get(slot) !== key) return;
  book.inFlight.delete(slot);
  notify();
}

/** Test support: forgets every book and every stored document. */
export function resetInvoiceJournalForTests(): void {
  books.clear();
  try {
    const stale: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) stale.push(key);
    }
    for (const key of stale) window.sessionStorage.removeItem(key);
  } catch {
    // Nothing stored, nothing to remove.
  }
  notify();
}

/** Test support: a page reload. The memory of the page is gone, the documents in `sessionStorage` stay. */
export function resetInvoiceJournalMemoryForReload(): void {
  books.clear();
  notify();
}
